/**
 * Hook para generar y persistir un ID único de usuario.
 * Usa cookie + localStorage para máxima persistencia.
 */

import { useEffect, useState } from "react";
import { BRAND_STORAGE_PREFIX } from "@/lib/brandConfig";

const USER_ID_COOKIE = `${BRAND_STORAGE_PREFIX}_uid`;
const USER_ID_STORAGE = `${BRAND_STORAGE_PREFIX}_user_id`;

function generateUserId(): string {
  return `uid_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Obtiene o crea un ID único de usuario persistente.
 * Prioridad: cookie > localStorage > generar nuevo
 */
export function useUserId(): string {
  const [userId, setUserId] = useState<string>(() => {
    // SSR safe: retornar placeholder durante server render
    if (typeof window === "undefined") return "";

    // 1. Intentar leer de cookie
    const cookieId = getCookie(USER_ID_COOKIE);
    if (cookieId) return cookieId;

    // 2. Intentar leer de localStorage
    try {
      const storageId = localStorage.getItem(USER_ID_STORAGE);
      if (storageId) {
        // Sincronizar a cookie
        setCookie(USER_ID_COOKIE, storageId);
        return storageId;
      }
    } catch (e) {
      // localStorage bloqueado
    }

    // 3. Generar nuevo ID
    const newId = generateUserId();
    try {
      localStorage.setItem(USER_ID_STORAGE, newId);
    } catch (e) {
      // localStorage bloqueado, solo usar cookie
    }
    setCookie(USER_ID_COOKIE, newId);
    return newId;
  });

  // Sincronizar userId entre cookie y localStorage en mount
  useEffect(() => {
    if (!userId) return;

    // Asegurar que ambos estén sincronizados
    const cookieId = getCookie(USER_ID_COOKIE);
    if (cookieId !== userId) {
      setCookie(USER_ID_COOKIE, userId);
    }

    try {
      const storageId = localStorage.getItem(USER_ID_STORAGE);
      if (storageId !== userId) {
        localStorage.setItem(USER_ID_STORAGE, userId);
      }
    } catch (e) {
      // localStorage bloqueado
    }
  }, [userId]);

  return userId;
}
